import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, RefreshCw, Calendar } from "lucide-react";
import { fetchBlogPosts } from "@/lib/adminApi";
import { Overline, MaskedLines } from "@/components/Motion";
import Seo from "@/components/Seo";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", { dateStyle: "long" });
  } catch {
    return iso;
  }
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setPosts(await fetchBlogPosts());
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="blog-page">
      <Seo title="Blog | Arivu Finserve — Financial Planning Insights" description="Practical insights on financial planning, retirement and investing from Avishaa Thakar of Arivu Finserve, Mumbai." />

      <section className="relative pt-40 pb-16 lg:pb-24 overflow-hidden bg-brand-navy">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Overline className="text-brand-sky">Insights &amp; ideas</Overline>
          </motion.div>
          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-8xl font-light leading-[0.95] mt-6">
            <MaskedLines lines={["Thinking clearly", "about your money."]} delay={0.3} />
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        {loading ? (
          <div className="flex items-center text-brand-muted"><RefreshCw className="h-5 w-5 animate-spin mr-3" /> Loading posts…</div>
        ) : posts.length === 0 ? (
          <p className="text-brand-muted text-lg font-light">No posts published yet — check back soon.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-list">
            {posts.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="group bg-white border border-brand-border rounded-2xl overflow-hidden hover:border-brand-green transition-colors flex flex-col"
              >
                <Link to={`/blog/${p.slug}`} className="block overflow-hidden">
                  {p.cover ? (
                    <img src={p.cover} alt="" className="w-full h-52 object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-52 bg-brand-navy" />
                  )}
                </Link>
                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center gap-2 text-brand-muted text-xs uppercase tracking-wider">
                    <Calendar className="h-3.5 w-3.5" /> {fmt(p.created_at)}
                  </div>
                  <Link to={`/blog/${p.slug}`}>
                    <h2 className="font-serif text-2xl text-brand-navy mt-3 leading-snug group-hover:text-brand-green transition-colors">{p.title}</h2>
                  </Link>
                  {p.summary && <p className="text-brand-muted font-light mt-3 leading-relaxed line-clamp-3 flex-grow">{p.summary}</p>}
                  {p.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {p.tags.map((t) => (
                        <span key={t} className="text-xs bg-brand-green/10 text-brand-green rounded-full px-3 py-1">{t}</span>
                      ))}
                    </div>
                  )}
                  <Link to={`/blog/${p.slug}`} className="inline-flex items-center gap-2 text-brand-navy font-medium mt-5 hover:gap-3 transition-all">
                    Read <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
